import { useEffect, useState } from 'react'
import { useSearchParams, useNavigate } from 'react-router-dom'
import { motion } from 'framer-motion'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Loader2, CheckCircle, XCircle, Mail, Sparkles } from 'lucide-react'
import { authAPI } from '@/lib/api/auth'
import { getApiErrorMessage } from '@/lib/api/errors'

const EmailVerificationPage = () => {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading')
  const [message, setMessage] = useState('')
  const token = searchParams.get('token')

  useEffect(() => {
    const verifyEmail = async () => {
      if (!token) {
        setStatus('error')
        setMessage('No verification token provided')
        return
      }

      try {
        const response = await authAPI.verifyEmail(token)
        setStatus('success')
        setMessage(response.data.message || 'Email verified successfully!')
      } catch (error) {
        setStatus('error')
        setMessage(getApiErrorMessage(error, 'Failed to verify email'))
      }
    }

    verifyEmail()
  }, [token])

  const handleContinueToLogin = () => {
    navigate('/auth')
  }

  return (
    <div className="min-h-screen bg-weave-dark text-weave flex items-center justify-center px-6">
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="w-full max-w-md"
      >
        {/* Header */}
        <div className="text-center mb-8">
          <div className="flex items-center justify-center gap-2 mb-4">
            <div className="w-10 h-10 bg-white rounded-lg flex items-center justify-center">
              <Sparkles className="w-6 h-6 text-weave-dark" />
            </div>
            <span className="text-2xl font-bold">EcoSemantic</span>
          </div>
          <h1 className="text-2xl font-bold mb-2">Email Verification</h1>
          <p className="text-weave-muted">
            {status === 'loading' && 'Verifying your email address...'}
            {status === 'success' && 'Your email has been verified!'}
            {status === 'error' && 'Verification failed'}
          </p>
        </div>

        <Card className="card-weave">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              {status === 'loading' && (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  Verifying...
                </>
              )}
              {status === 'success' && (
                <>
                  <CheckCircle className="w-5 h-5 text-green-500" />
                  Verification Successful
                </>
              )}
              {status === 'error' && (
                <>
                  <XCircle className="w-5 h-5 text-red-500" />
                  Verification Failed
                </>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Status Alert */}
            <Alert variant={status === 'error' ? 'destructive' : 'default'}>
              <Mail className="h-4 w-4" />
              <AlertDescription>{message}</AlertDescription>
            </Alert>

            {/* Success Actions */}
            {status === 'success' && (
              <div className="space-y-4">
                <div className="p-4 bg-green-50 dark:bg-green-900/20 rounded-lg border border-green-200 dark:border-green-800">
                  <p className="text-sm text-green-800 dark:text-green-200">
                    🎉 Welcome to EcoSemantic! Your account is now active and you can sign in to
                    access your LCA dashboard.
                  </p>
                </div>

                <Button onClick={handleContinueToLogin} className="btn-weave w-full">
                  Continue to Sign In
                </Button>
              </div>
            )}

            {/* Error Actions */}
            {status === 'error' && (
              <div className="space-y-4">
                <div className="p-4 bg-red-50 dark:bg-red-900/20 rounded-lg border border-red-200 dark:border-red-800">
                  <p className="text-sm text-red-800 dark:text-red-200 mb-2">
                    This verification link may have expired or already been used.
                  </p>
                  <p className="text-sm text-red-600 dark:text-red-300">
                    You can request a new verification email from the sign-in page.
                  </p>
                </div>

                <Button onClick={handleContinueToLogin} variant="outline" className="w-full">
                  Go to Sign In
                </Button>
              </div>
            )}

            {/* Loading State */}
            {status === 'loading' && (
              <div className="flex items-center justify-center py-8">
                <div className="flex items-center space-x-2">
                  <Loader2 className="w-6 h-6 animate-spin text-weave-accent" />
                  <span className="text-weave-muted">Processing verification...</span>
                </div>
              </div>
            )}

            {/* Back to Home Link */}
            <div className="text-center text-sm text-weave-muted">
              <a
                href="/"
                className="text-weave-accent hover:text-weave-accent/80 transition-colors"
              >
                ← Back to Home
              </a>
            </div>
          </CardContent>
        </Card>
      </motion.div>
    </div>
  )
}

export default EmailVerificationPage
